import type { BuildingRoofSquares, DesignData } from '../@types/types';
import designServices, {
    getRoofSquaresFromSystemDesign,
    roundedBasedOn33Percent,
    type RetrieveSystemDesignResponse,
} from '../services/oneButtonServices/designServices';
import type {
    BatteryDesignSource,
    BatterySelectOption,
    ResolvedBatteryDesign,
    ResolvedRoofSquares,
    RoofSquareSplit,
    RoofSquaresResolveInput,
    RoofSquaresSource,
} from './productDesignFallbacks.types';

export const DEFAULT_BATTERY_MANUFACTURER = 'Tesla';
export const DEFAULT_BATTERY_MODEL_NAME = 'Tesla Powerwall 3 AC';
export const DEFAULT_BATTERY_QUANTITY = 2;

const oneDrawRoofFetchCache = new Map<string, Promise<RoofSquareSplit | null>>();

function asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function finiteNumber(value: unknown): number | null {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function positiveRounded(value: unknown): number | null {
    const n = finiteNumber(value);
    if (n == null || n <= 0) return null;
    return Math.round(n);
}

function splitOrNull(low: number, standard: number): RoofSquareSplit | null {
    const l = Number.isFinite(low) ? Math.max(0, low) : 0;
    const s = Number.isFinite(standard) ? Math.max(0, standard) : 0;
    const total = l + s;
    if (total <= 0) return null;
    return { low: l, standard: s, total };
}

function splitFromTotal(total: number, sourceLow = 0, sourceStandard = 0): RoofSquareSplit | null {
    if (!(total > 0)) return null;
    if (sourceLow + sourceStandard > 0) {
        return {
            low: sourceLow,
            standard: sourceStandard,
            total: sourceLow + sourceStandard,
        };
    }
    return { low: 0, standard: total, total };
}

export function productTypeNeedsRoof(productType: string): boolean {
    const n = String(productType || '')
        .toLowerCase()
        .replace(/\s+/g, '');
    return n === 'roof' || n.includes('roof') || n === 'rr' || n === 'rrr';
}

export function productTypeNeedsBattery(productType: string): boolean {
    const n = String(productType || '')
        .toLowerCase()
        .replace(/\s+/g, '');
    return n === 'battery' || n.includes('battery') || n.includes('storage');
}

export function opportunityNeedsRoofSquares(products: string[] | null | undefined): boolean {
    return (products ?? []).some(productTypeNeedsRoof);
}

export function opportunityNeedsBatteryDesign(products: string[] | null | undefined): boolean {
    return (products ?? []).some(productTypeNeedsBattery);
}

export function countDesignArrayModules(systemDesign: unknown): number | null {
    const sd = asRecord(systemDesign);
    const arr = sd?.DesignArray;
    if (!Array.isArray(arr) || arr.length === 0) return null;
    let n = 0;
    for (const item of arr) {
        const row = asRecord(item);
        if (!row) continue;
        n += Number(row.Quantity ?? row.ModuleCount ?? row.Module_Quantity ?? row.Modules) || 0;
    }
    return n > 0 ? Math.round(n) : null;
}

export function sumOneDrawRoofSquareTable(table: BuildingRoofSquares[] | null | undefined): RoofSquareSplit | null {
    if (!Array.isArray(table) || table.length === 0) return null;

    const buildings = table.map((building) => {
        const rows = Array.isArray(building?.data) ? building.data : [];
        const usable = rows.filter((row) => row?.isRoofAccessory !== true);
        const anyActive = usable.some((row) => row?.isActive === true);
        const included = anyActive ? usable.filter((row) => row.isActive !== false) : usable;
        let low = 0;
        let standard = 0;
        for (const row of included) {
            low += Number(row.lowSlopeSquare) || 0;
            standard += Number(row.normalSlopeSquare) || 0;
        }
        return {
            low: Number(low.toFixed(2)),
            standard: Number(standard.toFixed(2)),
        };
    });

    const rawLow = buildings.reduce((sum, b) => sum + b.low, 0);
    const rawNormal = buildings.reduce((sum, b) => sum + b.standard, 0);
    // Match Roof Square Table → Total Site Summary (Low / Normal / Total).
    return splitOrNull(roundedBasedOn33Percent(rawLow), roundedBasedOn33Percent(rawNormal));
}

export function sumOneDrawDesignListSquares(designList: DesignData[] | DesignData | null | undefined): RoofSquareSplit | null {
    const list = Array.isArray(designList) ? designList : designList ? [designList] : [];
    const design = list[0];
    const buildings = design?.Buildings;
    if (!Array.isArray(buildings) || buildings.length === 0) return null;

    let low = 0;
    let standard = 0;
    for (const building of buildings) {
        const roofs = Array.isArray(building?.Roofs) ? building.Roofs : [];
        let buildingLow = 0;
        let buildingNormal = 0;
        for (const roof of roofs) {
            if ((roof as { Active_Ind?: boolean })?.Active_Ind === false) continue;
            buildingLow += Number(roof.Low_Square) || 0;
            buildingNormal += Number(roof.Normal_Square) || 0;
        }
        low += roundedBasedOn33Percent(buildingLow);
        standard += roundedBasedOn33Percent(buildingNormal);
    }
    return splitOrNull(low, standard);
}

export function parseBuildingRoofSquares(data: unknown, applyRoofWastage = false): RoofSquareSplit | null {
    let parsed = data;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return null;
        }
    }
    const root = asRecord(parsed);
    const buildings = Array.isArray(root?.Building)
        ? root.Building
        : Array.isArray(parsed)
          ? parsed
          : [];
    if (!Array.isArray(buildings) || buildings.length === 0) return null;

    const isActive = (v: unknown) => {
        if (v === false || v === 0 || v === '0' || String(v).toLowerCase() === 'false') return false;
        return v == null ? true : Boolean(v);
    };

    let low = 0;
    let standard = 0;
    for (const building of buildings) {
        const b = asRecord(building);
        const roofs = Array.isArray(b?.Roof) ? b.Roof : [];
        let buildingLow = 0;
        let buildingNormal = 0;
        for (const roof of roofs) {
            const r = asRecord(roof);
            if (!r || !isActive(r.Active_Ind)) continue;
            buildingLow += Number(r.Low_Square ?? r.LowSquare) || 0;
            buildingNormal += Number(r.Normal_Square ?? r.NormalSquare) || 0;
        }
        low += roundedBasedOn33Percent(buildingLow, applyRoofWastage);
        standard += roundedBasedOn33Percent(buildingNormal, applyRoofWastage);
    }
    return splitOrNull(low, standard);
}

export async function fetchOneDrawRoofSquares(options: {
    salesforceId?: string | null;
    designSource?: string | null;
    applyRoofWastage?: boolean;
}): Promise<RoofSquareSplit | null> {
    const sfid = String(options.salesforceId ?? '').trim();
    if (!sfid) return null;
    const cacheKey = `${sfid}::onedraw::${options.applyRoofWastage ? '1' : '0'}`;
    const existing = oneDrawRoofFetchCache.get(cacheKey);
    if (existing) return existing;

    const request = (async () => {
        try {
            const data = await designServices.RetrieveBuildingWithRoof(sfid);
            return parseBuildingRoofSquares(data, options.applyRoofWastage === true);
        } catch {
            return null;
        }
    })();

    oneDrawRoofFetchCache.set(cacheKey, request);
    return request;
}

function slopesFromSystemDesignRoof(systemDesign: RetrieveSystemDesignResponse | null): RoofSquareSplit | null {
    const roof = systemDesign?.Roof;
    if (roof == null) return null;
    const roofs = Array.isArray(roof) ? roof : [roof];
    let low = 0;
    let standard = 0;
    let total = 0;
    for (const r of roofs) {
        low += Number(r?.LowSlope ?? r?.Low_Slope_Override) || 0;
        standard += Number(r?.NormalSlope ?? r?.Normal_Slope_Override) || 0;
        total += Number(r?.RoofSquare ?? r?.TotalRoofSquares ?? r?.RoofSquares) || 0;
    }
    if (low + standard > 0) return splitOrNull(low, standard);
    const fromHelper = getRoofSquaresFromSystemDesign(systemDesign);
    if (fromHelper != null && fromHelper > 0) return splitFromTotal(fromHelper);
    if (total > 0) return splitFromTotal(total);
    return null;
}

export function resolveRoofSquares(input: RoofSquaresResolveInput): ResolvedRoofSquares {
    const empty: ResolvedRoofSquares = { low: 0, standard: 0, total: 0, source: null };
    if (input.includeRoofProduct === false) return empty;

    const ovLow = finiteNumber(input.priceQuoteOverrides?.lowSlopeOverride);
    const ovNormal = finiteNumber(input.priceQuoteOverrides?.normalSlopeOverride);
    const ovTotal = finiteNumber(input.priceQuoteOverrides?.roofSquareOverride);
    if (ovLow != null || ovNormal != null) {
        const split = splitOrNull(ovLow ?? 0, ovNormal ?? 0);
        if (split) return { ...split, source: 'override' };
    }
    if (ovTotal != null && ovTotal > 0) {
        const split = splitFromTotal(ovTotal);
        if (split) return { ...split, source: 'override' };
    }

    const pqLow = finiteNumber(input.priceQuoteDetails?.Low_Slope_Override);
    const pqNormal = finiteNumber(input.priceQuoteDetails?.Normal_Slope_Override);
    const pqTotal = finiteNumber(input.priceQuoteDetails?.RoofSquareOverride);
    if (pqLow != null || pqNormal != null) {
        const split = splitOrNull(pqLow ?? 0, pqNormal ?? 0);
        if (split) return { ...split, source: 'override' };
    }
    if (pqTotal != null && pqTotal > 0) {
        const split = splitFromTotal(pqTotal);
        if (split) return { ...split, source: 'override' };
    }

    const oneDraw = input.oneDrawTable ?? input.oneDrawDesignList ?? input.oneDrawFetched ?? null;
    if (oneDraw && oneDraw.total > 0) return { ...oneDraw, source: 'onedraw' };

    const opportunity = slopesFromSystemDesignRoof(input.systemDesign as RetrieveSystemDesignResponse | null);
    if (opportunity) return { ...opportunity, source: 'opportunity' };

    const modules =
        positiveRounded(input.designArrayModules) ?? countDesignArrayModules(input.systemDesign);
    if (modules != null && modules > 0) {
        return { low: 0, standard: modules, total: modules, source: 'design-array' };
    }

    return empty;
}

export async function resolveRoofSquaresWithFetch(
    input: RoofSquaresResolveInput & {
        salesforceId?: string | null;
        designSource?: string | null;
        applyRoofWastage?: boolean;
    },
): Promise<ResolvedRoofSquares> {
    const sync = resolveRoofSquares(input);
    if (sync.total > 0 || input.includeRoofProduct === false) return sync;

    const fetched = await fetchOneDrawRoofSquares({
        salesforceId: input.salesforceId,
        designSource: input.designSource,
        applyRoofWastage: input.applyRoofWastage,
    });
    return resolveRoofSquares({ ...input, oneDrawFetched: fetched });
}

export function roofSquaresSourceLabel(source: RoofSquaresSource): string | null {
    if (source === 'onedraw' || source === 'design-array') return 'Roof squares from oneDRAW';
    return null;
}

function batteryRawFromSystemDesign(systemDesign: unknown): {
    manufacturer: string;
    name: string;
    quantity: number | null;
} {
    const sd = asRecord(systemDesign) ?? {};
    const batteryInfo = asRecord(sd.BatteryInformation);
    const batteries = sd.DesignBattery ?? sd.Batteries;
    const bat0 =
        Array.isArray(batteries) && batteries[0] && typeof batteries[0] === 'object'
            ? asRecord(batteries[0])
            : null;

    const pick = (row: Record<string, unknown> | null) => ({
        manufacturer: String(row?.Manufacturer ?? row?.ManufacturerName ?? '').trim(),
        name: String(
            row?.BatteryModelName ?? row?.ModelName ?? row?.Name ?? row?.Model ?? '',
        ).trim(),
        quantity: positiveRounded(row?.Quantity),
    });

    if (batteryInfo && (batteryInfo.Manufacturer || batteryInfo.Name || batteryInfo.Quantity)) {
        return pick(batteryInfo);
    }
    if (bat0) return pick(bat0);

    const oppProducts = sd.OpportunityProductsFinanceInfo;
    if (Array.isArray(oppProducts)) {
        const row = oppProducts.find((p) => {
            const rec = asRecord(p);
            return String(rec?.Product_Type_Name ?? '').toLowerCase() === 'battery';
        });
        if (row) return pick(asRecord(row));
    }

    return { manufacturer: '', name: '', quantity: null };
}

export function resolveBatteryDesignForPricing(
    systemDesign: unknown,
    options?: { includeBatteryProduct?: boolean },
): ResolvedBatteryDesign {
    if (options?.includeBatteryProduct === false) {
        return { manufacturer: '', name: '', quantity: 0, source: 'opportunity' };
    }

    const raw = batteryRawFromSystemDesign(systemDesign);
    if (raw.quantity != null && raw.quantity > 0) {
        return {
            manufacturer: raw.manufacturer || DEFAULT_BATTERY_MANUFACTURER,
            name: raw.name || DEFAULT_BATTERY_MODEL_NAME,
            quantity: raw.quantity,
            source: 'opportunity',
        };
    }

    return {
        manufacturer: DEFAULT_BATTERY_MANUFACTURER,
        name: DEFAULT_BATTERY_MODEL_NAME,
        quantity: DEFAULT_BATTERY_QUANTITY,
        source: 'default',
    };
}

export function batteryDesignSourceLabel(source: BatteryDesignSource): string | null {
    if (source === 'default') {
        return `Default battery: ${DEFAULT_BATTERY_QUANTITY}× ${DEFAULT_BATTERY_MODEL_NAME}`;
    }
    return null;
}

/** Block pricing when enabled product lines lack roof squares or battery count fallbacks. */
export function validatePricingProductFallbacks(
    productLines: string[] | null | undefined,
    roof: ResolvedRoofSquares,
    battery: ResolvedBatteryDesign,
): string | null {
    const lines = productLines ?? [];
    if (opportunityNeedsRoofSquares(lines) && roof.total <= 0) {
        return 'Roof squares are required for pricing. Finalize roof design in oneDRAW, or include solar modules for a default square estimate.';
    }
    if (opportunityNeedsBatteryDesign(lines) && (!battery.quantity || battery.quantity <= 0)) {
        return `Battery count is required for pricing (default ${DEFAULT_BATTERY_QUANTITY} units when no battery design exists).`;
    }
    return null;
}

export function pickBatteryManufacturerValue(
    options: BatterySelectOption[] | null | undefined,
    preferred = DEFAULT_BATTERY_MANUFACTURER,
): string | undefined {
    const list = options ?? [];
    const want = preferred.trim().toLowerCase();
    const exact = list.find((o) => String(o.value).trim().toLowerCase() === want);
    if (exact) return String(exact.value);
    const labeled = list.find((o) => String(o.label).trim().toLowerCase() === want);
    return labeled ? String(labeled.value) : undefined;
}

export function pickBatteryModelValue(
    options: BatterySelectOption[] | null | undefined,
    preferred = DEFAULT_BATTERY_MODEL_NAME,
): string | undefined {
    const list = options ?? [];
    const want = preferred.trim().toLowerCase();
    const byLabel = list.find((o) => String(o.label).trim().toLowerCase() === want);
    if (byLabel) return String(byLabel.value);
    const byValue = list.find((o) => String(o.value).trim().toLowerCase() === want);
    if (byValue) return String(byValue.value);
    const includes = list.find((o) => String(o.label).toLowerCase().includes(want));
    return includes ? String(includes.value) : undefined;
}

export function pickBatteryQuantityValue(
    options: BatterySelectOption[] | null | undefined,
    preferred = DEFAULT_BATTERY_QUANTITY,
): string | undefined {
    const want = String(preferred);
    const list = options ?? [];
    const exact = list.find((o) => String(o.value).trim() === want);
    if (exact) return String(exact.value);
    return undefined;
}
